import { Buffer } from 'buffer';

import { useMemo, useState } from 'react';

import { Button, LinearProgress, MenuItem, Select, Typography } from '@mui/material';
import { isEmpty } from '@npi/buddy-utils';
import { useField } from 'formik';
import { FileRejection, useDropzone } from 'react-dropzone';

import { useMutation } from '@redwoodjs/web';

import { Flex } from 'src/components/Flex';
import { showErrorToast } from 'src/components/Toasts/toast';
import { GET_PRESIGNED_URL } from 'src/graphql/mutations/getPresignedURL.mutation';
import { UPLOAD_PDF } from 'src/graphql/mutations/uploadPdf.mutation';
import { LogError } from 'src/lib/error-logger';
import { stripStringSpaces } from 'src/utils/utils';

import { OptionTypes } from '../DropDown';

import FilePreviewer from './file-previewer';
import { processImage } from './file-upload-utils';

interface FileUploadDropDownProps {
    name: string;
    label?: string;
    fileTypes: OptionTypes[];
    placeholder?: string;
    maxFileSize?: number;
    acceptedFileTypes?: { [key: string]: string[] };
    isReadOnly?: boolean;
    hasBackup?: boolean;
    isNonSensitive?: boolean;
}
const byteToMB = (size: number) => {
    return (size / 1000000).toFixed(2);
};
const FileUploadDropDown = ({
    name,
    label,
    fileTypes,
    placeholder,
    maxFileSize = 20000000,
    acceptedFileTypes = {
        'image/jpeg': [
            '.jpeg',
            '.jpg',
            '.png',
            '.JPEG',
            '.JPG',
            '.PNG',
            '.Jpeg',
            '.Jpg',
            '.png',
            '.jPeg',
            '.jPg',
            '.Png',
            '.jpEg',
            '.jpg',
            '.pNg',
            '.jpeG',
            '.jpG',
            '.pnG',
        ],
    },
    isReadOnly,
    hasBackup = true,
    isNonSensitive = false,
}: FileUploadDropDownProps) => {
    const [{ value: file }, { error, touched }, { setValue: setFile }] = useField(name);
    const [{ value: fileType }, __, { setValue: setFileType }] = useField({
        name: `${name}.fileType`,
        defaultValue: '',
    });
    const [{ value: locationUrl }, ___, { setValue }] = useField({
        name: `${name}.locationUrl`,
        defaultValue: '',
    });

    const [uploading, setUploading] = useState(false);

    const [getPresignedURL] = useMutation(GET_PRESIGNED_URL, {
        onError: (err) => {
            return showErrorToast(err.message);
        },
    });

    const [uploadPdf] = useMutation(UPLOAD_PDF, {
        onError: (err) => {
            return showErrorToast(err.message);
        },
    });

    const readFileAsBuffer = async (file: File) => {
        return await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                if (reader.result instanceof ArrayBuffer) {
                    resolve(Buffer.from(reader.result));
                } else {
                    reject(new Error('Expected ArrayBuffer as result'));
                }
            };
            reader.onerror = reject;
            reader.readAsArrayBuffer(file);
        });
    };
    const hasError = useMemo(() => {
        return error && touched;
    }, [error, touched]);

    const onDrop = async (acceptedFiles, fileRejections: FileRejection[]) => {
        const unsupportedError = fileRejections[0]?.errors[0]?.message;
        try {
            if (unsupportedError?.length > 0) {
                if (unsupportedError === 'File type must be text/pdf,.pdf,.PDF,.Pdf,.pDf,.pdF') {
                    showErrorToast('File type must be pdf');
                } else if (
                    stripStringSpaces(unsupportedError) ===
                    stripStringSpaces(
                        'File type must be image/jpeg,.jpeg,.jpg,.png,.JPEG,.JPG,.PNG,.Jpeg,.Jpg,.png,.jPeg,.jPg,.Png,.jpEg,.jpg,.pNg,.jpeG,.jpG,.pnG'
                    )
                ) {
                    showErrorToast('File type must be jpg, jpeg, or png');
                } else {
                    showErrorToast('File type must be pdf, png, jpg, or jpeg');
                }
                return;
            }
            setUploading(true);
            const [acceptedFile] = acceptedFiles;
            const { name, type, size } = acceptedFile;
            if (size > maxFileSize) {
                const sizeInMb = byteToMB(size);
                const maxInMb = byteToMB(maxFileSize);
                const errorMsg = `File size of ${sizeInMb}MB exceed maximum allowable size of ${maxInMb}MB`;
                showErrorToast(errorMsg);
                return;
            }

            const bits = name.replaceAll(' ', '_').split('.');
            bits.pop();
            const nameOnly = bits.join('.');
            const fileUpload = {
                mimeType: type,
                fileName: name,
                fileSize: size,
                locationUrl: '',
            };

            const reader = new FileReader();
            reader.readAsDataURL(acceptedFile);

            let newFile: File;

            if (type === 'application/pdf') {
                const buffer = await readFileAsBuffer(acceptedFile);
                const convertedBuffer = (buffer as Buffer).toString('base64');
                const { data } = await uploadPdf({
                    variables: {
                        buffer: convertedBuffer,
                    },
                });
                const compressedBuffer = Buffer.from(data.uploadPdf.compressedBuffer, 'base64');
                const compressedFile = new File([compressedBuffer], `${nameOnly}.pdf`, {
                    type: 'application/pdf',
                });
                newFile = compressedFile;
            } else {
                reader.onload = () => {
                    const imageUrl = reader.result;
                    const image = document.createElement('img');
                    image.src = imageUrl as string;

                    image.onload = () => {
                        newFile = processImage(image, nameOnly, size, acceptedFile);
                    };
                };
            }

            const { data } = await getPresignedURL({
                variables: {
                    input: {
                        fileName: name,
                        mimeType: fileUpload.mimeType,
                        hasBackup,
                        fileSize: fileUpload.fileSize,
                        referer: window.location.pathname.substring(1),
                        isNonSensitive,
                    },
                },
            });

            if (data?.getSecureUploadUrl?.backupSignedUrl) {
                const putBackupFile = await fetch(data.getSecureUploadUrl?.backupSignedUrl, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': type,
                        'X-Amz-Tagging': data?.getSecureUploadUrl?.tags,
                    },
                    body: acceptedFile,
                }); // Do not await this background proccess as this is uploading the uncompressed version that the user do not need to wait for
                if (putBackupFile.status !== 200) {
                    throw new Error('Failed to upload backup file');
                }
            }

            if (data?.getSecureUploadUrl?.signedUrl) {
                const putUploadFile = await fetch(data.getSecureUploadUrl.signedUrl, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': type,
                    },
                    body: newFile,
                });
                if (putUploadFile.status !== 200) {
                    throw new Error('Failed to upload file');
                }
            }

            fileUpload.locationUrl = data?.getSecureUploadUrl.signedUrl?.split('?')[0];

            setFile({ ...newFile, ...fileUpload });
        } catch (error) {
            showErrorToast('Failed to upload file');
            LogError(error.message);
        } finally {
            setUploading(false);
        }
    };

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        maxFiles: 1,
        multiple: false,
        accept: acceptedFileTypes,
    });
    return (
        <Flex flexDirection="column" gap="8px">
            {label && (
                <Typography color="#1D1D1B" fontWeight={600} fontSize="14px" marginBottom="13px">
                    {label}
                </Typography>
            )}
            <Flex
                padding="8px"
                borderRadius="8px"
                border="1px solid #B4B4B3"
                maxHeight="53px"
                alignItems="center"
                overflow="hidden"
            >
                <Select
                    value={fileType}
                    onChange={({ target: { value } }) => {
                        setFileType(value);
                    }}
                    placeholder={placeholder}
                    label={placeholder}
                    displayEmpty
                    renderValue={(value) => {
                        if (isEmpty(value)) {
                            return <span style={{ opacity: 0.6 }}>{placeholder}</span>;
                        }
                        return value?.replace(/_/g, ' ');
                    }}
                    sx={{
                        flex: 1,
                        border: 'none',
                        '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                        '& fieldset': {
                            border: 'none',
                        },
                    }}
                >
                    <MenuItem disabled value="">
                        {placeholder}
                    </MenuItem>
                    {fileTypes.map(({ label, value }) => {
                        return (
                            <MenuItem key={value} value={value}>
                                {label.replace(/_/g, ' ')}
                            </MenuItem>
                        );
                    })}
                </Select>
                {(fileType?.length > 0 || locationUrl) && (
                    <Flex {...getRootProps()}>
                        <input {...getInputProps()} />
                        <Button
                            variant="contained"
                            sx={{
                                color: '#fff',
                                borderRadius: '8px',
                                minWidth: '100px',
                                boxShadow: 'none',
                                '&:hover': {
                                    boxShadow: 'none',
                                },
                            }}
                            component="span"
                            disabled={isReadOnly && uploading}
                        >
                            Select File
                        </Button>
                    </Flex>
                )}
            </Flex>
            {uploading && <LinearProgress />}
            {((file?.locationUrl && file?.fileName) || locationUrl) && (
                <FilePreviewer
                    onRemove={
                        isReadOnly
                            ? undefined
                            : () => {
                                  return setValue(undefined);
                              }
                    }
                />
            )}
            {hasError && <label className="error">{error}</label>}
        </Flex>
    );
};

export default FileUploadDropDown;
